import React, { useEffect } from "react";
import { BehaviorSubject } from "rxjs";
const observers = {};
/**
 * A wrapper for browser storage that maintains state across multiple hook instances.
 * - Supports localStorage, sessionStorage, or custom storage implementations.
 * - Allows for custom parsing and stringifying of stored values.
 * - Shares state between multiple hook instances using the same key.
 * @param key A unique storage key to identify the state.
 * @param options Configuration options for storage, parsing, stringifying, and fallback value.
 * @returns A tuple containing the stored value and a function to update it.
 */
export const useMadStorage = (key, { parse = defaultParseValue, stringify = JSON.stringify, storage = window.sessionStorage, fallback } = {}) => {
    let subscription;
    let observer = observers[key];
    let storedValue = observer ? observer.value : parse(storage.getItem(key));
    if (storedValue === undefined) {
        storedValue = fallback;
    }
    const [data, setDataState] = React.useState(typeof storedValue !== "undefined" ? storedValue : undefined);
    // `key` can change between renders (e.g. lazy activation resolving to a real proxy key).
    // Re-derive the value for the new key immediately, rather than returning `data` for the
    // previous key until the effect below re-subscribes on the next render.
    const [resolvedKey, setResolvedKey] = React.useState(key);
    let currentData = data;
    if (resolvedKey !== key) {
        setResolvedKey(key);
        currentData = typeof storedValue !== "undefined" ? storedValue : undefined;
        setDataState(currentData);
    }
    if (!observer) {
        observers[key] = new BehaviorSubject(typeof currentData !== "undefined" ? currentData : undefined);
        observer = observers[key];
    }
    useEffect(() => {
        subscription = observer.subscribe(setDataState);
        return () => {
            subscription.unsubscribe();
            if (!observer.observed) {
                delete observers[key];
            }
        };
    }, [key]);
    /**
     * Saves data to storage and notifies observers.
     * @param value value to save - if undefined, removeItem
     */
    function saveData(value) {
        if (typeof value === "undefined") {
            storage.removeItem(key);
            return observer.next(fallback);
        }
        storage.setItem(key, stringify(value));
        return observer.next(value);
    }
    return [currentData, saveData];
};
/**
 * Default parser function that parses JSON values.
 * @param value Stored value
 * @returns {T | undefined} Parsed value
 */
function defaultParseValue(value) {
    try {
        return value ? JSON.parse(value) : undefined;
    }
    catch {
        console.warn("useMadStorage: Failed to parse stored value.", value);
        return undefined;
    }
}
useMadStorage.getObserverCount = () => Object.keys(observers).length;
//# sourceMappingURL=madStorage.js.map